import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Checkbox,
    Typography,
} from '@mui/material';
import { ReactComponent as CheckboxUncheckedIcon } from '../../../assets/images/ui/checkboxUnchecked.svg';
import { ReactComponent as CheckboxCheckedIcon } from '../../../assets/images/ui/checkboxChecked.svg';
import SmartCaptcha from './SmartCaptcha';
import AuthTextField from './AuthTextField';
import AuthConsentLabel from './AuthConsentLabel';
import {
    AUTH_FIELD_LIMITS,
    getLoginFieldHint,
    getPasswordConfirmHint,
    getPasswordFieldHelperText,
    getPasswordFieldHint,
} from './authValidation';
import { authConsentRowSx, authErrorSx, authPrimaryButtonSx } from './authDialogStyles';

const RegisterTab = ({ onSubmit, loading, apiError, onClearApiError, captchaEnabled = true }) => {
    const { t } = useTranslation();
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [consent, setConsent] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const [captchaResetKey, setCaptchaResetKey] = useState(0);
    const [fieldError, setFieldError] = useState('');
    const [touched, setTouched] = useState({
        login: false,
        password: false,
        passwordConfirm: false,
    });

    const loginHint = getLoginFieldHint(login, touched.login);
    const passwordHint = getPasswordFieldHint(password, touched.password);
    const passwordHelper = getPasswordFieldHelperText(password);
    const passwordConfirmHint = getPasswordConfirmHint(password, passwordConfirm, touched.passwordConfirm);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFieldError('');
        setTouched({ login: true, password: true, passwordConfirm: true });

        if (loginHint || passwordHint || passwordConfirmHint) {
            return;
        }
        if (!consent) {
            setFieldError(t('Необходимо согласие с условиями'));
            return;
        }
        if (!captchaToken) {
            setFieldError(t('Подтвердите, что вы не робот'));
            return;
        }

        try {
            await onSubmit({
                login,
                password,
                password_confirm: passwordConfirm,
                captcha_token: captchaToken,
                consent,
            });
        } catch {
            setCaptchaToken('');
            setCaptchaResetKey((value) => value + 1);
        }
    };

    const isFormValid = login.trim().length >= AUTH_FIELD_LIMITS.loginMinLength
        && login.trim().length <= AUTH_FIELD_LIMITS.loginMaxLength
        && password.length >= AUTH_FIELD_LIMITS.passwordMinLength
        && password.length <= AUTH_FIELD_LIMITS.passwordMaxLength
        && passwordConfirm.length >= AUTH_FIELD_LIMITS.passwordMinLength
        && password === passwordConfirm
        && consent
        && Boolean(captchaToken);

    const displayError = fieldError || apiError?.message;

    return (
        <Box
            component='form'
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: '16px', alignSelf: 'stretch' }}
        >
            <AuthTextField
                label={t('Логин')}
                value={login}
                onChange={(e) => {
                    setLogin(e.target.value);
                    onClearApiError?.();
                }}
                onBlur={() => setTouched((prev) => ({ ...prev, login: true }))}
                error={Boolean(loginHint) || Boolean(apiError?.fields?.login)}
                helperText={loginHint ? t(loginHint) : undefined}
                required
                autoComplete='username'
                placeholder={t('Введите логин')}
            />
            <AuthTextField
                label={t('Пароль')}
                type='password'
                value={password}
                onChange={(e) => {
                    setPassword(e.target.value);
                    onClearApiError?.();
                }}
                onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
                error={passwordHelper.isError || Boolean(apiError?.fields?.password)}
                helperText={passwordHelper.text ? t(passwordHelper.text) : undefined}
                required
                autoComplete='new-password'
                placeholder={t('Введите пароль')}
            />
            <AuthTextField
                label={t('Подтверждение пароля')}
                type='password'
                value={passwordConfirm}
                onChange={(e) => {
                    setPasswordConfirm(e.target.value);
                    onClearApiError?.();
                }}
                onBlur={() => setTouched((prev) => ({ ...prev, passwordConfirm: true }))}
                error={Boolean(passwordConfirmHint) || Boolean(apiError?.fields?.passwordConfirm)}
                helperText={passwordConfirmHint ? t(passwordConfirmHint) : undefined}
                required
                autoComplete='new-password'
                placeholder={t('Повторите пароль')}
            />
            <SmartCaptcha
                enabled={captchaEnabled}
                resetKey={captchaResetKey}
                onToken={setCaptchaToken}
            />
            {displayError && (
                <Typography sx={authErrorSx}>
                    {displayError}
                </Typography>
            )}
            <Box
                component='label'
                sx={authConsentRowSx}
            >
                <Checkbox
                    checked={consent}
                    onChange={(e) => {
                        setConsent(e.target.checked);
                        if (e.target.checked) {
                            setFieldError('');
                        }
                    }}
                    size='small'
                    icon={<CheckboxUncheckedIcon />}
                    checkedIcon={<CheckboxCheckedIcon />}
                />
                <AuthConsentLabel />
            </Box>
            <Box sx={{ pt: '4px' }}>
                <Button type='submit' variant='contained' disabled={loading || !isFormValid} sx={authPrimaryButtonSx}>
                    {t('Зарегистрироваться')}
                </Button>
            </Box>
        </Box>
    );
};

export default RegisterTab;
